'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { htmlSeguro } from '../../utils/htmlSeguro';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useNode, useEditor } from '@craftjs/core';
import useClientStore from '@/store/useClientStore';
import { rubroDelPais } from '@/lib/country';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { useEnsureVehicles } from '@/hooks/useEnsureVehicles';
import useVehicleFiltersStore from '@/store/useVehicleFiltersStore';
import { resolveNavLink } from '@/utils/functions';
import { Search } from 'lucide-react';
import { MegaNavbar } from '@/components/builder2/sections/layout/MegaNavbar';
import { AreaDePiezas, Pieza } from '../../utils/piezaLibre';
import {
  EstilosFondoMovil,
  FondoDelHero,
  PropsFondoMovil,
  atributosCajaFondoMovil,
  usaFondoMovil,
} from '../../utils/fondoResponsive';

interface NavLink {
  text: string;
  url: string;
}

interface HeroMegaProps extends PropsFondoMovil {
  links?: NavLink[];
  ctaText?: string;
  ctaUrl?: string;
  logoUrl?: string;
  logoHeight?: number;
  showLogo?: boolean;
  titleLine1?: string;
  titleLine2?: string;
  backgroundImage?: string;
  overlayColor?: string;
  overlayOpacity?: number;
  textColor?: string;
  navTextColor?: string;
  ctaBgColor?: string;
  ctaTextColor?: string;
  searchButtonColor?: string;
  showSearch?: boolean;
  heroHeight?: number;
  fullWidth?: boolean;
}

const ChevronDown = ({ color }: { color: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const HeroMega = ({
  links = [
    { text: 'Inicio', url: '/' },
    { text: 'Stock disponible', url: '/vehicles' },
    { text: 'Consignación', url: '/consignments' },
    { text: 'Compramos tu auto', url: '/buy-direct' },
    { text: 'Quiénes somos', url: '/about' },
    { text: 'Contacto', url: '/contact' },
  ],
  ctaText = 'Contacto',
  ctaUrl = '/contact',
  logoUrl = '',
  logoHeight = 44,
  showLogo = true,
  titleLine1 = 'Encuentra tu',
  titleLine2 = 'Próximo vehículo',
  backgroundImage = 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=1920',
  overlayColor = '#0b1120',
  overlayOpacity = 0.45,
  textColor = '#ffffff',
  navTextColor = '#ffffff',
  ctaBgColor = '',
  ctaTextColor = '#ffffff',
  searchButtonColor = '#dc2626',
  showSearch = true,
  heroHeight = 640,
  fullWidth = false,
  mobileImage,
  mobileFit,
  mobilePosition,
  mobileHeight,
  mobileBackdropColor,
}: HeroMegaProps) => {
  const { connectors } = useNode();
  const { isEnabled } = useEditor((state) => ({ isEnabled: state.options.enabled }));
  const { client } = useClientStore();
  const { t } = useTranslation();
  const { vehicles } = useEnsureVehicles();
  const { setFilters } = useVehicleFiltersStore();
  const router = useRouter();

  const [brandId, setBrandId] = useState('');
  const [modelId, setModelId] = useState('');

  // Reset model when brand changes
  useEffect(() => { setModelId(''); }, [brandId]);

  const primaryColor = client?.theme?.light?.primary || '#dc2626';
  const finalCtaBgColor = ctaBgColor || primaryColor;
  const companyName = client?.name || rubroDelPais(client?.country);
  const finalLogoUrl = logoUrl || client?.logo_dark || client?.logo || '';

  // Marcas/modelos derivados del stock real (mismo origen que /vehicles)
  const brands = useMemo(() => {
    const m = new Map<string, string>();
    vehicles.forEach((v: any) => {
      const id = v?.brand?.id;
      if (id != null && !m.has(String(id))) m.set(String(id), v.brand.name);
    });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [vehicles]);

  const models = useMemo(() => {
    if (!brandId) return [] as [string, string][];
    const m = new Map<string, string>();
    vehicles.forEach((v: any) => {
      if (String(v?.brand?.id) !== brandId) return;
      const id = v?.model?.id;
      if (id != null && !m.has(String(id))) m.set(String(id), v.model.name);
    });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [vehicles, brandId]);

  const handleNavClick = (e: React.MouseEvent) => {
    if (isEnabled) e.preventDefault();
  };

  const handleSearch = () => {
    if (isEnabled) return;
    const f: Record<string, any> = {};
    if (brandId) f.brand = [brandId];
    if (modelId) f.model = [String(modelId)];
    setFilters(f);
    router.push('/vehicles');
  };

  const containerWidth = fullWidth ? 'max-w-full' : 'max-w-7xl';

  const propsMovil: PropsFondoMovil = {
    mobileImage, mobileFit, mobilePosition, mobileHeight, mobileBackdropColor,
  };
  const { style: varsMovil, ...atributosMovil } = atributosCajaFondoMovil(propsMovil) as {
    style?: React.CSSProperties; [k: string]: unknown;
  };

  return (
    <section
      ref={(el: HTMLElement | null) => { if (el) connectors.connect(el); }}
      {...(atributosMovil as Record<string, string>)}
      style={{
        color: textColor,
        minHeight: `${heroHeight}px`,
        position: 'relative',
        ...varsMovil,
      }}
      className="w-full flex flex-col"
    >
      {usaFondoMovil(propsMovil) && <EstilosFondoMovil />}
      {/* Fondo: antes iba como `background-image` de esta misma caja. */}
      <FondoDelHero imagen={backgroundImage} mobileImage={mobileImage} prioridad />

      {/* Overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{ backgroundColor: overlayColor, opacity: overlayOpacity }}
      />

      {/* Todo lo que va EN FLUJO, en un solo envoltorio. En escritorio es un
          único hijo `flex-1` de la columna, o sea lo mismo de antes; en móvil
          con `mobileFit="adapt"` la sección pasa a ser una grilla de una celda
          y este envoltorio es lo que se apila ENCIMA de la imagen, sin perder
          su propia columna. No lleva `z-index` a propósito: crear acá un
          contexto de apilado dejaría al navbar `fixed` encerrado adentro. */}
      <div className="flex w-full flex-1 flex-col">
      {/* Spacer: el MegaNavbar es fixed en público (overlay); reponemos su alto
          (h-16) para que el contenido del hero no quede tapado. En el editor el
          MegaNavbar es relative (en flujo) y no hace falta spacer. */}
      {!isEnabled && <div aria-hidden="true" style={{ height: 64 }} />}

      {/* Navbar del hero, compartido con el resto de rutas (ver MegaNavbar). */}
      <MegaNavbar
        hasHero
        isEditor={isEnabled}
        links={links}
        ctaText={ctaText}
        ctaUrl={ctaUrl}
        logoUrl={finalLogoUrl}
        showLogo={showLogo}
        logoHeight={logoHeight}
        companyName={companyName}
        navTextColor={navTextColor}
        ctaBgColor={finalCtaBgColor}
        ctaTextColor={ctaTextColor}
        fullWidth={fullWidth}
        onNavClick={handleNavClick}
      />

      {/* Hero content */}
      <AreaDePiezas className={`relative z-10 flex-1 flex flex-col justify-center ${containerWidth} w-full mx-auto px-4 sm:px-6 lg:px-8 py-12`}>
        <Pieza clave="titulo" tipo="texto" etiqueta="Título">
          <h1 className="max-w-3xl" style={{ color: textColor }}>
            {titleLine1 && (
              <span
                className="block text-3xl sm:text-4xl md:text-5xl font-light tracking-tight"
                dangerouslySetInnerHTML={htmlSeguro(titleLine1)}
              />
            )}
            {titleLine2 && (
              <span
                className="block text-4xl sm:text-5xl md:text-7xl font-extrabold tracking-tight mt-1"
                dangerouslySetInnerHTML={htmlSeguro(titleLine2)}
              />
            )}
          </h1>
        </Pieza>
        <Pieza clave="buscador" etiqueta="Buscador">
          {showSearch && (
            <div className="mt-8 w-full max-w-2xl">
              <div className="bg-white rounded-2xl shadow-2xl p-2 flex flex-col sm:flex-row items-stretch gap-2">
                {/* Marca */}
                <div className="relative flex-1">
                  <select
                    value={brandId}
                    onChange={(e) => setBrandId(e.target.value)}
                    disabled={isEnabled}
                    className="w-full appearance-none bg-gray-50 text-gray-700 rounded-xl pl-4 pr-9 py-3.5 text-sm font-medium outline-none cursor-pointer focus:ring-2 focus:ring-gray-200"
                  >
                    <option value="">{t('site.vehicles.brand')}</option>
                    {brands.map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                  <ChevronDown color="#6b7280" />
                </div>
                {/* Modelo */}
                <div className="relative flex-1">
                  <select
                    value={modelId}
                    onChange={(e) => setModelId(e.target.value)}
                    disabled={isEnabled || !brandId}
                    className="w-full appearance-none bg-gray-50 text-gray-700 rounded-xl pl-4 pr-9 py-3.5 text-sm font-medium outline-none cursor-pointer focus:ring-2 focus:ring-gray-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <option value="">{t('site.hero.model')}</option>
                    {models.map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                  <ChevronDown color="#6b7280" />
                </div>
                {/* Buscar */}
                <button
                  onClick={handleSearch}
                  className="flex items-center justify-center rounded-xl px-5 py-3.5 transition-opacity hover:opacity-90 sm:w-14"
                  style={{ backgroundColor: searchButtonColor }}
                  aria-label={t('site.hero.search')}
                >
                  <Search size={20} color="#ffffff" />
                </button>
              </div>
            </div>
          )}
        </Pieza>
      </AreaDePiezas>
      </div>
    </section>
  );
};

HeroMega.craft = {
  displayName: 'Mega Cabecera',
  props: {
    links: [
      { text: 'Inicio', url: '/' },
      { text: 'Stock disponible', url: '/vehicles' },
      { text: 'Consignación', url: '/consignments' },
      { text: 'Compramos tu auto', url: '/buy-direct' },
      { text: 'Quiénes somos', url: '/about' },
      { text: 'Contacto', url: '/contact' },
    ],
    ctaText: 'Contacto',
    ctaUrl: '/contact',
    logoUrl: '',
    logoHeight: 44,
    showLogo: true,
    titleLine1: 'Encuentra tu',
    titleLine2: 'Próximo vehículo',
    backgroundImage: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=1920',
    overlayColor: '#0b1120',
    overlayOpacity: 0.45,
    textColor: '#ffffff',
    navTextColor: '#ffffff',
    ctaBgColor: '',
    ctaTextColor: '#ffffff',
    searchButtonColor: '#dc2626',
    showSearch: true,
    heroHeight: 640,
    fullWidth: false,
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => false,
  },
};
